/** Formateadores y utilidades de presentación de Reportes (es-CO). */
import type {
  ReportColumn,
  ReportColumnType,
  ReportFilterOption,
} from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';

/** Valor de un filtro tal como viaja en `ReportRunRequest.filtros` (objeto libre). */
export type ReportFilterValues = Record<string, unknown>;

/**
 * Valor de un filtro de rango; la clave del filtro es la columna. Número para
 * `numero`/`moneda` y `YYYY-MM-DD` para `fecha`.
 */
export interface ReportRangeValue {
  desde?: number | string;
  hasta?: number | string;
}

/**
 * Valor centinela de la opción "sin filtro" (`Todos`/`Todas`) de un filtro
 * categórico. Sólo existe en el formulario: nunca se guarda en los filtros ni
 * viaja al backend.
 */
export const TODOS = '__todos__';

/** Opción "sin filtro" de un filtro categórico, con la etiqueta del contrato. */
export function opcionTodos(columna: ReportColumn): ReportFilterOption {
  return { valor: TODOS, etiqueta: columna.filtro?.etiquetaVacio ?? 'Todos' };
}

/** `true` si el valor es "vacío" y no debe viajar al backend. */
function isEmptyValue(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

/** `true` si un rango no tiene ni `desde` ni `hasta`. */
function isEmptyRange(value: unknown): boolean {
  if (!value || typeof value !== 'object') return true;
  const { desde, hasta } = value as ReportRangeValue;
  return isEmptyValue(desde) && isEmptyValue(hasta);
}

/**
 * Descarta filtros vacíos antes de ejecutar. Un filtro declarado pero sin valor
 * no debe llegar como `undefined`/`''`/`{ desde: undefined, hasta: undefined }`:
 * el backend valida contra su tipo.
 */
export function pruneFilters(values: ReportFilterValues): ReportFilterValues {
  const result: ReportFilterValues = {};
  for (const [key, value] of Object.entries(values)) {
    if (value !== null && typeof value === 'object') {
      if (!isEmptyRange(value)) result[key] = value;
    } else if (!isEmptyValue(value) && value !== TODOS) {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Quita los filtros activos de columnas filtrables que dejaron de estar
 * seleccionadas. Devuelve los valores resultantes y las claves removidas.
 */
export function dropFiltersForHiddenColumns(
  values: ReportFilterValues,
  columnas: ReportColumn[],
  columnasSeleccionadas: string[],
): { filtros: ReportFilterValues; removidos: string[] } {
  const activos = pruneFilters(values);
  const seleccionadas = new Set(columnasSeleccionadas);
  const removidos = columnas
    .filter(
      (columna) =>
        columna.filtro !== undefined &&
        !seleccionadas.has(columna.clave) &&
        columna.clave in activos,
    )
    .map((columna) => columna.clave);

  if (removidos.length === 0) return { filtros: values, removidos };

  const limpios = { ...values };
  for (const clave of removidos) delete limpios[clave];
  return { filtros: limpios, removidos };
}

function formatNumber(value: unknown): string {
  const number = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number)) return '—';
  return new Intl.NumberFormat('es-CO', { maximumFractionDigits: 2 }).format(number);
}

function formatCurrency(value: unknown): string {
  const number = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number)) return '—';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(number);
}

function formatDate(value: unknown): string {
  if (!value) return '—';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Fecha y hora de una ejecución del historial (es-CO). */
export function formatDateTime(value: unknown): string {
  if (!value) return '—';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Formatea una celda según el `tipo` declarado por la columna. En un catálogo se
 * muestra la etiqueta de la opción; si no hay coincidencia (o falta la lista de
 * opciones, típico de un catálogo por `origen` aún sin cargar) se muestra el valor.
 */
export function formatCell(
  value: unknown,
  tipo: ReportColumnType,
  opciones?: ReportFilterOption[],
): string {
  if (value === undefined || value === null || value === '') return '—';
  switch (tipo) {
    case 'numero':
      return formatNumber(value);
    case 'moneda':
      return formatCurrency(value);
    case 'fecha':
      return formatDate(value);
    case 'catalogo': {
      const opcion = opciones?.find((candidata) => candidata.valor === String(value));
      return opcion?.etiqueta ?? String(value);
    }
    case 'texto':
      return String(value);
  }
}

/** Alineación de la columna según su tipo (números y moneda a la derecha). */
export function columnAlignment(tipo: ReportColumnType): 'left' | 'right' {
  return tipo === 'numero' || tipo === 'moneda' ? 'right' : 'left';
}

/** Mensaje real del backend (mismo criterio que las demás features). */
export function reportErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * Mensaje de la descarga del artefacto según el contrato:
 * 409 = todavía generándose, 410 = expiró, 404 = ajeno o inexistente.
 */
export function artifactErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) {
      return 'El archivo todavía se está generando. Esperá a que la ejecución termine.';
    }
    if (error.status === 410) {
      return 'El archivo expiró. Volvé a generar el reporte.';
    }
    if (error.status === 404) {
      return 'No se encontró el archivo de esta ejecución.';
    }
    return error.message;
  }
  return reportErrorMessage(error, 'No se pudo descargar el archivo.');
}
